/* Lead landingpage: navigation, presentation and direct Meta Lead tracking.
   Klaviyo owns validation, newsletter consent, submission and confirmation text.
   Reuses the existing Meta pixel only after explicit tracking consent. */
(() => {
  'use strict';
  const rootSelector = '[data-lead-landingpage]';

  const metaPixelId = '4277533558970788';
  const trackedForms = new Set();

  function trackingConsent() {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.currentVisitorConsent !== 'function'
      || typeof privacy.marketingAllowed !== 'function'
      || typeof privacy.analyticsProcessingAllowed !== 'function') return null;
    try {
      const consent = privacy.currentVisitorConsent();
      return consent.marketing === 'yes' && consent.analytics === 'yes'
        && privacy.marketingAllowed() === true
        && privacy.analyticsProcessingAllowed() === true;
    } catch (_) { return null; }
  }

  function trackSignup(formId) {
    const key = String(formId);
    if (trackedForms.has(key)) return;
    trackedForms.add(key);
    const randomId = window.crypto?.randomUUID?.()
      || Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
    const eventId = 'lead-landingpage-' + randomId;
    let attempts = 0;
    const retry = () => {
      if (attempts < 40) window.setTimeout(attempt, 250);
      else console.warn('[Lead Landingpage] Meta Lead was not sent: pixel or consent API unavailable.');
    };
    const attempt = () => {
      attempts += 1;
      const consent = trackingConsent();
      // A refusal ends this attempt. A later consent grant does not replay it.
      if (consent === false) return;
      if (consent !== true) { retry(); return; }
      const fbq = window.fbq;
      if (typeof fbq !== 'function' || typeof fbq.callMethod !== 'function') {
        retry(); return;
      }
      try {
        // Reuse the existing SDK and pixel initialization, with no new PageView.
        fbq('trackSingle', metaPixelId, 'Lead', {
          content_name: 'Lead Landingpage',
          lead_source: 'landingpage',
          form_id: key,
          page_path: window.location.pathname
        }, {eventID: eventId});
      } catch (_) {
        console.warn('[Lead Landingpage] Meta Lead call failed.');
      }
    };
    // Never copy the email address, other form fields or URL query parameters.
    attempt();
  }

  function showSubmittedState(root) {
    const signup = root.querySelector('.lead-landingpage__signup');
    const embed = root.querySelector('[data-lead-landingpage-klaviyo]');
    if (!signup || !embed || signup.dataset.leadLandingpageSubmitted === 'true') return false;

    signup.dataset.leadLandingpageSubmitted = 'true';
    signup.removeAttribute('aria-labelledby');
    signup.setAttribute('role', 'group');
    signup.setAttribute('aria-label', 'Anmeldung abgeschlossen');

    // This decoration stays outside Klaviyo's own rendered content.
    const icon = document.createElement('span');
    icon.className = 'lead-landingpage__success-icon';
    icon.setAttribute('aria-hidden', 'true');

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('focusable', 'false');
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10');
    circle.setAttribute('stroke-width', '1.1');
    const check = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    check.setAttribute('points', '7.5 12 10.75 15.25 16.75 9.25');
    check.setAttribute('stroke-width', '1.8');
    svg.append(circle, check);
    icon.append(svg);
    embed.before(icon);
    return true;
  }

  window.addEventListener('klaviyoForms', event => {
    const detail = event.detail;
    if (detail?.type !== 'submit' || !detail.formId) return;
    const formClass = 'klaviyo-form-' + detail.formId;
    document.querySelectorAll(rootSelector).forEach(root => {
      const embed = root.querySelector('[data-lead-landingpage-klaviyo]');
      // Match this page's configured embed, rather than another shop popup.
      if (embed?.firstElementChild?.classList.contains(formClass)) {
        if (showSubmittedState(root)) trackSignup(detail.formId);
      }
    });
  });

  function init(scope) {
    const roots = scope.matches?.(rootSelector)
      ? [scope]
      : scope.querySelectorAll(rootSelector);
    roots.forEach(root => {
      if (root.dataset.leadLandingpageReady === 'true') return;
      root.dataset.leadLandingpageReady = 'true';
      root.addEventListener('click', event => {
        const link = event.target.closest('a[data-lead-landingpage-signup]');
        if (!link || !root.contains(link) || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const signup = root.querySelector('.lead-landingpage__signup');
        if (!signup) return;
        event.preventDefault();
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const compact = window.matchMedia('(max-width: 820px)').matches;
        const email = signup.dataset.leadLandingpageSubmitted === 'true' ? null
          : signup.querySelector('input[type="email"]:not([disabled]):not([readonly])');
        const target = email && email.getClientRects().length ? email : signup;

        // Keep focus inside the original user click, without timers or await.
        // Mobile browsers may then show the software keyboard themselves.
        target.focus({preventScroll: true});
        const scrollTarget = compact && target === email ? email : signup;
        scrollTarget.scrollIntoView({block: 'center', behavior: reduced || compact ? 'instant' : 'smooth'});
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), {once: true});
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
